import { useCallback, useEffect, useState } from "react";
import { theme, card, btnPrimary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { fmtDateOnly } from "../lib/dateOnly";
import { StatusTag, type StatusTone } from "./Drawing";
import { secondaryBtn } from "../pages/resilience/shared";

// Re-assessment before renewal on a supplier's Contracts tab (docs/TPRM-CONTRACTS.md "Re-assessment before
// renewal"). Every figure comes from the server (GET /tprm/suppliers/:id/renewals, lib/supplierRenewal.ts): the
// notice deadline is contractTiming()'s, the window is the operating parameter supplier.renewal_reassess_days. The
// questionnaire goes through the ordinary questionnaire routes, from the supplier's tier set; the decision is
// POST /tprm/suppliers/:id/contracts/:contractId/renewal-decision.

type Stage = "upcoming" | "open" | "decided" | "locked" | "lapsed";
type QState = "none" | "draft" | "sent" | "returned";

export interface RenewalDecisionRow {
  id: string;
  contractId: string;
  noticeDeadline: string;
  decision: "renew" | "renegotiate" | "exit" | string;
  reason: string;
  decidedAt: string;
  decidedByName?: string | null;
}

export interface RenewalView {
  contractId: string;
  title: string;
  autoRenew: boolean;
  endDate: string | null;
  noticeDeadline: string;
  windowOpensAt: string;
  daysToNotice: number;
  stage: Stage;
  decision: RenewalDecisionRow | null;
  questionnaire: { id: string; status: string; sentAt: string | null; returnedAt: string | null; riskScore: number | null } | null;
  questionnaireState: QState;
  signalsRefreshedAt: string | null;
}

interface RenewalsResponse { reassessDays: number; autoSend: boolean; renewals: RenewalView[]; decisions: RenewalDecisionRow[] }

const DECISIONS = [
  { value: "renew", label: "Renew" },
  { value: "renegotiate", label: "Renegotiate" },
  { value: "exit", label: "Exit" },
] as const;
const decisionLabel = (d: string) => DECISIONS.find((x) => x.value === d)?.label ?? d;

const STAGE: Record<Stage, { tone: StatusTone; label: string }> = {
  upcoming: { tone: "none", label: "Not due yet" },
  open: { tone: "partial", label: "Re-assessment due" },
  decided: { tone: "met", label: "Decision recorded" },
  locked: { tone: "gap", label: "Renewed with no decision" },
  lapsed: { tone: "gap", label: "Deadline passed with no decision" },
};

const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "5px 12px", fontSize: 12 };

function questionnaireText(v: RenewalView): string {
  const q = v.questionnaire;
  switch (v.questionnaireState) {
    case "none": return "No questionnaire sent for this renewal yet.";
    case "draft": return "A questionnaire is drafted on the Assessments tab and not yet sent.";
    case "sent": return `Questionnaire sent${q?.sentAt ? ` on ${fmtDateOnly(q.sentAt)}` : ""}; not returned yet.`;
    case "returned": return `Questionnaire returned${q?.returnedAt ? ` on ${fmtDateOnly(q.returnedAt)}` : ""}${q?.riskScore != null ? `, scored ${q.riskScore}%` : ""}.`;
  }
}

export function SupplierRenewalPanel({ supplier, contractId, canWrite, onChanged, onOpenTab }: {
  supplier: { id: string; riskTier: string; contactEmail?: string | null };
  /** Narrow to one agreement (its own view); otherwise every agreement whose re-assessment is open or overdue. */
  contractId?: string;
  canWrite: boolean;
  onChanged?: () => void;
  onOpenTab?: (tab: "assurance" | "assessments") => void;
}) {
  const [data, setData] = useState<RenewalsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ contractId: string; decision: string; reason: string } | null>(null);

  const load = useCallback(() => {
    api.get<RenewalsResponse>(`/tprm/suppliers/${supplier.id}/renewals`)
      .then(setData)
      .catch((e) => setError(apiErrorMessage(e, "Could not load the renewal re-assessments.")));
  }, [supplier.id]);
  useEffect(() => { load(); }, [load]);

  async function sendQuestionnaire(v: RenewalView) {
    const to = supplier.contactEmail ? ` to ${supplier.contactEmail}` : "";
    if (!window.confirm(`Send ${v.title}'s renewal questionnaire${to}? It uses the supplier's ${supplier.riskTier}-tier question set.`)) return;
    setBusy(`send:${v.contractId}`); setError(null);
    try {
      const created = await api.post<{ questionnaire: { id: string } }>(`/tprm/suppliers/${supplier.id}/questionnaires`, {
        title: `Renewal re-assessment: ${v.title}`.slice(0, 200),
        builtinTier: ["low", "medium", "high", "critical"].includes(supplier.riskTier) ? supplier.riskTier : "medium",
        dueDate: v.noticeDeadline,
      });
      await api.post(`/tprm/questionnaires/${created.questionnaire.id}/send`, {});
      load(); onChanged?.();
    } catch (e) { setError(apiErrorMessage(e, "Could not send the questionnaire.")); }
    finally { setBusy(null); }
  }

  async function saveDecision(e: React.FormEvent) {
    e.preventDefault();
    if (!deciding) return;
    setBusy(`decide:${deciding.contractId}`); setError(null);
    try {
      await api.post(`/tprm/suppliers/${supplier.id}/contracts/${deciding.contractId}/renewal-decision`, { decision: deciding.decision, reason: deciding.reason });
      setDeciding(null);
      load(); onChanged?.();
    } catch (err) { setError(apiErrorMessage(err, "Could not record the decision.")); }
    finally { setBusy(null); }
  }

  if (!data) return error ? <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 12 }}>{error}</div> : null;
  const shown = data.renewals.filter((v) => (contractId ? v.contractId === contractId : v.stage !== "upcoming"));
  const history = data.decisions.filter((d) => !contractId || d.contractId === contractId);
  // Decisions other than the one each view shows as current (an earlier term, or a decision since changed).
  const earlier = history.filter((d) => !shown.some((v) => v.decision?.id === d.id));
  if (!shown.length && !(contractId && earlier.length)) return null;

  return (
    <section style={{ ...card, padding: 0, marginBottom: 14, minWidth: 0 }} aria-label="Re-assessment before renewal">
      <header style={{ padding: "8px 14px", borderBottom: "1px solid var(--c-line)" }}>
        <h3 style={{ ...text.sectionTitle, margin: 0 }}>Re-assessment before renewal</h3>
      </header>
      <div style={{ padding: "10px 14px", display: "grid", gap: 12 }}>
        <div style={{ ...text.hint }}>
          {data.reassessDays} days before an agreement's notice deadline, re-assess the supplier and record whether you renew, renegotiate or exit.
          {data.autoSend ? " The questionnaire is sent automatically when the window opens." : " The questionnaire is sent only when someone here sends it."}
        </div>
        {shown.map((v) => {
          const s = STAGE[v.stage];
          const isDeciding = deciding?.contractId === v.contractId;
          const active = v.stage === "open" || v.stage === "locked" || v.stage === "lapsed";
          return (
            <div key={v.contractId} style={{ border: `1px solid ${v.stage === "locked" ? "var(--c-danger)" : "var(--c-border)"}`, borderRadius: 3, padding: "10px 12px" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {!contractId && <span style={{ ...text.body, fontWeight: 600 }}>{v.title}</span>}
                <StatusTag tone={s.tone}>{s.label}</StatusTag>
                <span style={{ ...text.hint }}>
                  Notice deadline {fmtDateOnly(v.noticeDeadline)}
                  {v.daysToNotice >= 0 ? ` (${v.daysToNotice === 0 ? "today" : `${v.daysToNotice} days`})` : ` (passed ${-v.daysToNotice} days ago)`}
                  {v.autoRenew ? " · renews automatically" : " · ends unless renewed"}
                </span>
              </div>
              {v.stage === "locked" && (
                <div style={{ ...text.hint, color: "var(--c-dangerText)", marginTop: 6 }}>
                  The notice window closed with no decision, so the agreement renews{v.endDate ? ` on ${fmtDateOnly(v.endDate)}` : ""}. Record the decision; to leave, ask the supplier for an early exit.
                </div>
              )}
              {v.stage === "upcoming" && <div style={{ ...text.hint, marginTop: 6 }}>The re-assessment opens on {fmtDateOnly(v.windowOpensAt)}.</div>}
              {v.stage !== "upcoming" && !v.decision && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18, ...text.hint, color: theme.color.text, lineHeight: 1.7 }}>
                  <li>{questionnaireText(v)}</li>
                  <li>
                    {v.signalsRefreshedAt ? `Public-record checks refreshed on ${fmtDateOnly(v.signalsRefreshedAt)}.` : "Public-record checks are refreshed by the next daily supplier monitor."}
                    {onOpenTab && <> <button type="button" onClick={() => onOpenTab("assurance")} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "var(--c-accentText)", cursor: "pointer" }}>See the signals</button></>}
                  </li>
                </ul>
              )}
              {v.decision && (
                <div style={{ ...text.hint, color: theme.color.text, marginTop: 6 }}>
                  <strong style={{ fontWeight: 600 }}>{decisionLabel(v.decision.decision)}</strong>, recorded {fmtDateOnly(v.decision.decidedAt)}{v.decision.decidedByName ? ` by ${v.decision.decidedByName}` : ""}: {v.decision.reason}
                </div>
              )}

              {canWrite && !isDeciding && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {active && v.questionnaireState === "none" && (
                    <button type="button" style={smallBtn} disabled={busy !== null} onClick={() => sendQuestionnaire(v)}>
                      {busy === `send:${v.contractId}` ? "Sending…" : "Send re-assessment questionnaire"}
                    </button>
                  )}
                  {(v.questionnaireState === "draft" || v.questionnaireState === "returned") && onOpenTab && (
                    <button type="button" style={smallBtn} onClick={() => onOpenTab("assessments")}>Open the questionnaire</button>
                  )}
                  <button type="button" style={v.decision ? smallBtn : { ...btnPrimary, padding: "5px 12px", fontSize: 12 }} disabled={busy !== null}
                    onClick={() => { setError(null); setDeciding({ contractId: v.contractId, decision: v.decision?.decision ?? "renew", reason: "" }); }}>
                    {v.decision ? "Change the decision" : "Record renewal decision"}
                  </button>
                </div>
              )}
              {isDeciding && deciding && (
                <form onSubmit={saveDecision} style={{ display: "grid", gap: 8, marginTop: 10 }}>
                  <fieldset style={{ border: "none", padding: 0, margin: 0, display: "flex", gap: 14, flexWrap: "wrap" }}>
                    <legend style={{ ...text.hint, marginBottom: 4 }}>Decision for the term ending {v.endDate ? fmtDateOnly(v.endDate) : "at the end date"}</legend>
                    {DECISIONS.map((d) => (
                      <label key={d.value} style={{ ...text.body, display: "flex", gap: 6, alignItems: "center" }}>
                        <input type="radio" name={`decision-${v.contractId}`} value={d.value} checked={deciding.decision === d.value} onChange={() => setDeciding({ ...deciding, decision: d.value })} />
                        {d.label}
                      </label>
                    ))}
                  </fieldset>
                  <label style={{ ...text.hint, display: "grid", gap: 4 }}>Reason
                    <textarea required minLength={10} rows={3} value={deciding.reason} onChange={(e) => setDeciding({ ...deciding, reason: e.target.value })}
                      placeholder="What the re-assessment showed and why this is the right course" style={{ ...inputStyle, fontSize: 13, resize: "vertical" }} />
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="submit" disabled={busy !== null} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }}>{busy ? "Saving…" : "Record decision"}</button>
                    <button type="button" onClick={() => setDeciding(null)} style={smallBtn}>Cancel</button>
                  </div>
                </form>
              )}
            </div>
          );
        })}
        {contractId && earlier.length > 0 && (
          <div>
            <div style={{ ...text.hint, fontWeight: 600, marginBottom: 4 }}>Earlier decisions</div>
            <ul style={{ margin: 0, paddingLeft: 18, ...text.hint, lineHeight: 1.7 }}>
              {earlier.map((d) => (
                <li key={d.id}>{decisionLabel(d.decision)} for the deadline of {fmtDateOnly(d.noticeDeadline)}, recorded {fmtDateOnly(d.decidedAt)}{d.decidedByName ? ` by ${d.decidedByName}` : ""}: {d.reason}</li>
              ))}
            </ul>
          </div>
        )}
        {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
      </div>
    </section>
  );
}
